/**
 @license
 Copyright (c) 2015-2026 Lablup Inc. All rights reserved.
 */
import { useSuspendedBackendaiClient } from '.';
import { useStartSessionCreationQuery } from '../__generated__/useStartSessionCreationQuery.graphql';
import { transformPortValuesToNumbers } from '../components/PortSelectFormItem';
import {
  RESOURCE_ALLOCATION_INITIAL_FORM_VALUES,
  UNIFIED_SLOT_TAG_PREFIX,
  isUnifiedAcceleratorSlot,
} from '../components/SessionFormItems/ResourceAllocationFormItems';
import {
  SessionLauncherFormValue,
  SessionResources,
} from '../pages/SessionLauncherPage';
import { useSetBAINotification } from './useBAINotification';
import {
  useCurrentProjectValue,
  useCurrentResourceGroupState,
} from './useCurrentProject';
import { useResolveImageReference } from './useDefaultImagesWithFallback';
import { generateRandomString, toGlobalId } from 'backend.ai-ui';
import * as _ from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { fetchQuery, graphql, useRelayEnvironment } from 'react-relay';

// Type for successful session creation result
type SessionCreationSuccess = {
  kernelId?: string;
  sessionId: string;
  sessionName: string;
  servicePorts: Array<{ name: string }>;
};
interface CreateSessionInfo {
  kernelName: string;
  sessionName: string;
  // Left `undefined` when the image reference carries no `@arch` suffix, so
  // that `createIfNotExists()` applies its own `x86_64` default. Passing an
  // empty string instead would suppress that default and send a blank
  // architecture to the manager.
  architecture: string | undefined;
  batchTimeout?: string;
  resources: SessionResources;
}

export const SESSION_LAUNCHER_NOTI_PREFIX = 'session-launcher:';

// Field names that have default values in SessionLauncherFormValue
type SessionLauncherDefaultFields =
  | 'sessionType'
  | 'allocationPreset'
  | 'hpcOptimization'
  | 'batch'
  | 'envvars'
  | 'resourceGroup';

// Custom type for environments - mutually exclusive fields
type StartSessionEnvironments = {
  environments:
    | {
        version: string;
        environment?: never;
        image?: never;
        manual?: never;
        customizedTag?: never;
      }
    | {
        version?: never;
        environment: string;
        image?: never;
        manual?: never;
        customizedTag?: never;
      }
    | {
        version?: never;
        environment?: never;
        image?: never;
        manual: string;
        customizedTag?: never;
      };
};

// Type that makes certain fields optional while keeping others required
export type StartSessionValue =
  // Required fields (environments with only version required)
  StartSessionEnvironments &
    // Optional fields from SessionLauncherFormValue
    Partial<Omit<SessionLauncherFormValue, 'environments'>>;

// Type for minimum required values (all fields except those with defaults are required)
export type StartSessionWithDefaultValue = Omit<
  StartSessionValue,
  | SessionLauncherDefaultFields
  | keyof typeof RESOURCE_ALLOCATION_INITIAL_FORM_VALUES
> &
  DeepPartial<
    Pick<
      StartSessionValue,
      | SessionLauncherDefaultFields
      | keyof typeof RESOURCE_ALLOCATION_INITIAL_FORM_VALUES
    >
  > & {
    dependencies?: string[];
  };

export type StartSessionResults = {
  fulfilled?: PromiseFulfilledResult<SessionCreationSuccess>[];
  rejected?: PromiseRejectedResult[];
};

export const useStartSession = () => {
  'use memo';

  const { t } = useTranslation();
  const currentProject = useCurrentProjectValue();
  const { upsertNotification } = useSetBAINotification();

  const relayEnv = useRelayEnvironment();
  const resolveImageReference = useResolveImageReference();
  const baiClient = useSuspendedBackendaiClient();
  const supportsMountById = baiClient.supports('mount-by-id');
  const supportBatchTimeout = baiClient?.supports('batch-timeout') ?? false;

  const [currentGlobalResourceGroup] = useCurrentResourceGroupState();

  const defaultFormValues: DeepPartial<SessionLauncherFormValue> = {
    sessionType: 'interactive',
    // If you set `allocationPreset` to 'custom', `allocationPreset` is not changed automatically any more.
    allocationPreset: 'auto-select',
    hpcOptimization: {
      autoEnabled: true,
    },
    batch: {
      enabled: false,
      command: undefined,
      scheduleDate: undefined,
      ...(supportBatchTimeout && {
        timeoutEnabled: false,
        timeout: undefined,
        timeoutUnit: 's',
      }),
    },
    envvars: [],
    // set default_session_environment only if set
    ...(baiClient._config?.default_session_environment && {
      environments: normalizeEnvironmentFormat(
        baiClient._config.default_session_environment,
      ),
    }),
    ..._.omit(RESOURCE_ALLOCATION_INITIAL_FORM_VALUES, ['resource']),
    resourceGroup: currentGlobalResourceGroup || undefined,
  };

  const startSessionWithDefault = (
    minimumValues: StartSessionWithDefaultValue,
  ) => {
    const mergedValue = _.merge({}, defaultFormValues, minimumValues);
    return startSession(
      mergedValue as SessionLauncherFormValue & { dependencies?: string[] },
    );
  };
  const startSession = async (
    values: SessionLauncherFormValue & { dependencies?: string[] },
  ) => {
    // A manual image (the `allowManualImageNameForSession` escape hatch) is
    // used verbatim — the user explicitly typed it, so it must not be
    // rewritten by resolution. `environment` holds a name-only reference (see
    // `normalizeEnvironmentFormat`); config values such as
    // `defaultImportEnvironment` may omit the tag and/or the architecture, so
    // resolve them against the registered image list here to make every
    // caller of `startSession` behave like the launcher form (FR-3462).
    const imageFullName =
      values.environments.manual ||
      (await resolveImageReference(
        values.environments.version || values.environments.environment,
      ));
    // `architecture` stays `undefined` for a reference without `@arch` on
    // purpose — see the note on `CreateSessionInfo.architecture`.
    const [kernelName = '', architecture] = imageFullName?.split('@') ?? [];

    const sessionName = _.isEmpty(values.sessionName)
      ? generateSessionId()
      : values.sessionName;

    const sessionInfo: CreateSessionInfo = {
      // Basic session information
      sessionName: sessionName,
      kernelName,
      architecture,
      resources: {
        enqueueOnly: true,
        // Project and domain settings
        group_name: values.owner?.enabled
          ? values.owner.project
          : currentProject.name || undefined,
        domain: values.owner?.enabled
          ? values.owner.domainName
          : baiClient._config.domainName,

        // Session configuration
        type: values.sessionType,
        // Convert multi-node x1 to single-node x1 since they are functionally
        // equivalent but multi-node requires overlay network which may not be
        // configured in all-in-one environments (FR-2381)
        cluster_mode:
          values.cluster_mode === 'multi-node' && values.cluster_size === 1
            ? 'single-node'
            : values.cluster_mode,
        cluster_size: values.cluster_size,
        maxWaitSeconds: 15,
        reuseIfExists: values.reuseIfExists ?? false,

        // Owner settings (optional)
        // FYI, `config.scaling_group` also changes based on owner settings
        ...(values.owner?.enabled
          ? {
              owner_access_key: values.owner.accesskey,
            }
          : {}),

        // Batch mode settings (optional)
        ...(values.sessionType === 'batch'
          ? {
              starts_at: values.batch.enabled
                ? values.batch.scheduleDate
                : undefined,
              startupCommand: values.batch.command,
            }
          : {}),

        // Bootstrap script (optional)
        ...(values.bootstrap_script
          ? { bootstrap_script: values.bootstrap_script }
          : {}),

        // Batch timeout configuration (optional)
        ...(supportBatchTimeout &&
        values?.batch?.timeoutEnabled &&
        !_.isUndefined(values?.batch?.timeout)
          ? {
              batchTimeout:
                _.toString(values.batch.timeout) + values?.batch?.timeoutUnit,
            }
          : undefined),

        // Session dependencies
        ...(values.dependencies && values.dependencies.length > 0
          ? { dependencies: values.dependencies }
          : {}),

        // Temporary marker for unified-memory sessions. There is currently no
        // dedicated field to tell from session info alone whether a session
        // uses a unified-memory accelerator, so tag it as
        // `unified-slot:<accelerator slot name>` (e.g. `unified-slot:cuda.unified`).
        ...(isUnifiedAcceleratorSlot(values.resource?.acceleratorType)
          ? {
              tag: `${UNIFIED_SLOT_TAG_PREFIX}${values.resource?.acceleratorType}`,
            }
          : {}),

        config: {
          // Resource allocation
          ...(values?.resource && {
            resources: {
              cpu: values?.resource?.cpu,
              mem: values?.resource?.mem,
              // Add accelerator only if specified. Unified-memory slots
              // auto-allocate from the shared host pool, so never send an
              // accelerator quantity for them.
              ...(values.resource?.acceleratorType &&
              values.resource?.accelerator &&
              values.resource?.accelerator > 0 &&
              !isUnifiedAcceleratorSlot(values.resource.acceleratorType)
                ? {
                    [values.resource.acceleratorType]:
                      values.resource.accelerator,
                  }
                : undefined),
            },
          }),
          scaling_group: values.owner?.enabled
            ? values.owner.resourceGroup
            : values.resourceGroup,
          ...(values?.resource && {
            resource_opts: {
              shmem: values?.resource?.shmem,
              // allow_fractional_resource_fragmentation can be added here if needed
            },
          }),

          // Storage configuration
          [supportsMountById ? 'mount_ids' : 'mounts']: values.mount_ids,
          [supportsMountById ? 'mount_id_map' : 'mount_map']:
            values.mount_id_map,

          // Environment variables
          environ: {
            ..._.fromPairs(values.envvars.map((v) => [v.variable, v.value])),
            // set hpcOptimization options: "OMP_NUM_THREADS", "OPENBLAS_NUM_THREADS"
            ...(values.hpcOptimization.autoEnabled
              ? {}
              : _.omit(values.hpcOptimization, 'autoEnabled')),
          },

          // Networking
          preopen_ports: transformPortValuesToNumbers(values.ports),

          // Agent selection (optional)
          ...(baiClient.supports('agent-select') &&
          !baiClient?._config?.hideAgents &&
          values.agent !== undefined &&
          !_.isEqual(_.castArray(values.agent), ['auto'])
            ? {
                // Filter out undefined values
                agent_list: _.filter(
                  _.castArray(values.agent),
                  (agent): agent is string => !!agent,
                ),
              }
            : undefined),
        },
      },
    };
    const sessionPromises = _.map(_.range(values.num_of_sessions || 1), (i) => {
      const formattedSessionName =
        (values.num_of_sessions || 1) > 1
          ? `${sessionInfo.sessionName}-${generateRandomString()}-${i}`
          : sessionInfo.sessionName;
      return baiClient
        .createIfNotExists(
          sessionInfo.kernelName,
          formattedSessionName,
          sessionInfo?.resources,
          undefined,
          sessionInfo.architecture,
        )
        .then(
          (res: { created: boolean; status: string; sessionId: string }) => {
            return res;
          },
        )
        .catch((err: any) => {
          if (err?.message?.includes('The session already exists')) {
            throw new Error(t('session.launcher.SessionAlreadyExists'));
          } else {
            throw err;
          }
        });
    });

    return Promise.allSettled(sessionPromises).then(
      async (
        sessionCreations: PromiseSettledResult<SessionCreationSuccess>[],
      ) => {
        // Group session creations by their status
        const results = _.groupBy(
          sessionCreations,
          'status',
        ) as StartSessionResults;
        return results;
      },
    );
  };

  const upsertSessionNotification = async (
    successCreations: PromiseFulfilledResult<SessionCreationSuccess>[],
    upsertNotificationOverrides?: Parameters<typeof upsertNotification>,
  ) => {
    const promises = _.map(successCreations, (creation) => {
      const session = creation.value;
      return fetchQuery<useStartSessionCreationQuery>(
        relayEnv,
        graphql`
          query useStartSessionCreationQuery($id: GlobalIDField!) {
            compute_session_node(id: $id) {
              ...BAINodeNotificationItemFragment
            }
          }
        `,
        {
          id: toGlobalId('ComputeSessionNode', session.sessionId),
        },
      )
        .toPromise()
        .then((queryResult) => {
          const createdSession = queryResult?.compute_session_node ?? null;

          if (createdSession) {
            upsertNotification(
              {
                key: `${SESSION_LAUNCHER_NOTI_PREFIX}${session.sessionId}`,
                node: createdSession,
                open: true,
                duration: 0,
                ...upsertNotificationOverrides?.[0],
              },
              upsertNotificationOverrides?.[1],
            );
          }
        });
    });
    return Promise.allSettled(promises);
  };

  return {
    startSession,
    startSessionWithDefault,
    defaultFormValues,
    upsertSessionNotification,
  };
};

const generateSessionId = () => {
  let text = '';
  const possible =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  for (let i = 0; i < 8; i++) {
    text += possible.charAt(Math.floor(Math.random() * possible.length));
  }
  return text + '-session';
};

const normalizeEnvironmentFormat = (environment: string) => {
  if (environment.includes(':')) {
    return {
      version: environment,
    };
  } else {
    return {
      environment: environment,
    };
  }
};
